import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import confetti from 'canvas-confetti';
import Header from '../components/Header';
import MessageFlowVisualization from '../components/MessageFlowVisualization';

interface QuestionStat {
  question_id: string;
  correct_count: number;
  total_answered: number;
  correct_percentage: number;
  average_time_seconds: number;
  median_time_seconds: number;
  answer_distribution: { [key: string]: number };
  time_buckets: { [key: string]: number };
}

interface AggregateStats {
  total_players: number;
  total_questions: number;
  class_average_score: number;
  class_average_percent: number;
  median_score: number;
  std_deviation: number;
  score_distribution: { [key: string]: number };
}

interface ProblematicPlayer {
  player_id: string;
  average_lag_ms: number;
  issue_count: number;
  affected_questions: string[];
}

interface ProblematicQuestion {
  question_id: string;
  average_lag_ms: number;
  players_affected: number;
}

interface TimingEvent {
  player_id: string;
  question_id: string;
  server_question_sent: string;
  client_question_received: string;
  client_answer_sent: string;
  server_answer_received: string;
  server_reveal_sent: string;
  network_lag_ms: number;
  effective_time_ms: number;
}

interface NetworkHealthSummary {
  average_network_lag_ms: number;
  median_lag_ms: number;
  std_dev_lag_ms: number;
  sample_count: number;
  problematic_players?: ProblematicPlayer[];
  problematic_questions?: ProblematicQuestion[];
}

interface GameStatistics {
  result_id: string;
  game_pin: string;
  played_at: string;
  duration_minutes: number;
  quiz_snapshot: any;
  aggregate_stats: AggregateStats;
  question_stats: QuestionStat[];
  player_results: any[];
  network_health_summary?: NetworkHealthSummary;
  timing_events?: TimingEvent[];
}

const QuizStatistics: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const statistics = location.state?.statistics as GameStatistics;

  const [networkHealthCollapsed, setNetworkHealthCollapsed] = useState(true);
  const [perQuestionCollapsed, setPerQuestionCollapsed] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);

  // Debug logging for network health
  useEffect(() => {
    if (statistics) {
      console.log('📊 Statistics received:', {
        hasNetworkHealth: !!statistics.network_health_summary,
        networkHealthSummary: statistics.network_health_summary,
        playerCount: statistics.aggregate_stats?.total_players,
        questionCount: statistics.aggregate_stats?.total_questions
      });
    }
  }, [statistics]);

  // Confetti celebration for good scores!
  useEffect(() => {
    if (!statistics) return;

    if (statistics.aggregate_stats.class_average_percent >= 70) {
      // Initial burst
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      // Extra celebration for exceptional scores
      if (statistics.aggregate_stats.class_average_percent >= 85) {
        setTimeout(() => {
          confetti({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0 }
          });
          confetti({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1 }
          });
        }, 250);
      }
    }
  }, [statistics]);

  // Group timing events by question
  const timingEventsByQuestion = useMemo(() => {
    if (!statistics?.timing_events) return new Map<string, TimingEvent[]>();

    const grouped = new Map<string, TimingEvent[]>();
    statistics.timing_events.forEach((event) => {
      if (!grouped.has(event.question_id)) {
        grouped.set(event.question_id, []);
      }
      grouped.get(event.question_id)!.push(event);
    });

    return grouped;
  }, [statistics?.timing_events]);

  // Calculate per-question timing stats
  const questionTimingStats = useMemo(() => {
    const stats = new Map<string, { avgLatency: number; maxLatency: number; playerCount: number; issueCount: number }>();

    timingEventsByQuestion.forEach((events, questionId) => {
      const latencies = events.map(e => e.network_lag_ms);
      const avgLatency = latencies.reduce((sum, lat) => sum + lat, 0) / latencies.length;
      const maxLatency = Math.max(...latencies);
      const issueCount = latencies.filter(lat => lat > 1000).length;

      stats.set(questionId, {
        avgLatency,
        maxLatency,
        playerCount: events.length,
        issueCount,
      });
    });

    return stats;
  }, [timingEventsByQuestion]);

  // Toggle question expansion
  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Handle expand/collapse all
  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedQuestions(new Set());
    } else {
      const allQuestionIds = Array.from(timingEventsByQuestion.keys());
      setExpandedQuestions(new Set(allQuestionIds));
    }
    setExpandAll(!expandAll);
  };

  if (!statistics) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600">No Statistics Available</h1>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { aggregate_stats, question_stats, quiz_snapshot } = statistics;

  // Prepare score distribution data
  const scoreDistData = Object.entries(aggregate_stats.score_distribution).map(([range, count]) => ({
    range,
    count,
  }));

  // Prepare question difficulty data (for heat map)
  const questionDifficultyData = question_stats.map((q, idx) => {
    const questionText = quiz_snapshot.questions[idx]?.question || `Question ${idx + 1}`;
    return {
      index: idx + 1,
      name: `Q${idx + 1}`,
      correctPercent: q.correct_percentage,
      avgTime: q.average_time_seconds,
      difficulty: q.correct_percentage >= 80 ? 'easy' : q.correct_percentage >= 50 ? 'medium' : 'hard',
      questionText: questionText.length > 50 ? questionText.substring(0, 50) + '...' : questionText,
      fullText: questionText,
    };
  });

  // Get difficulty color
  const getDifficultyColor = (percentage: number) => {
    if (percentage >= 80) return '#22c55e'; // green
    if (percentage >= 50) return '#eab308'; // yellow
    return '#ef4444'; // red
  };

  // Get network health status and color
  const getNetworkHealthStatus = () => {
    if (!statistics.network_health_summary) {
      return { status: 'No Data', color: 'gray', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' };
    }

    const hasProblematicPlayers = statistics.network_health_summary.problematic_players &&
                                   statistics.network_health_summary.problematic_players.length > 0;
    const hasProblematicQuestions = statistics.network_health_summary.problematic_questions &&
                                     statistics.network_health_summary.problematic_questions.length > 0;

    if (!hasProblematicPlayers && !hasProblematicQuestions) {
      return { status: 'Excellent', color: 'green', bgColor: 'bg-green-50', borderColor: 'border-green-200' };
    }

    const totalIssues = (statistics.network_health_summary.problematic_players?.length || 0) +
                       (statistics.network_health_summary.problematic_questions?.length || 0);

    if (totalIssues >= 3) {
      return { status: 'Poor', color: 'red', bgColor: 'bg-red-50', borderColor: 'border-red-200' };
    }

    return { status: 'Fair', color: 'yellow', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' };
  };

  // Identify questions that need review (< 60% correct)
  const questionsNeedingReview = questionDifficultyData.filter(q => q.correctPercent < 60);
  const strongQuestions = questionDifficultyData.filter(q => q.correctPercent >= 80);

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes popIn {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            transform: scale(1.15);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gray-50">
        <Header />

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Quiz Statistics</h1>
              <p className="text-gray-600">{quiz_snapshot.title}</p>
              <p className="text-sm text-gray-500">
                Played: {new Date(statistics.played_at).toLocaleString()} • Duration: {statistics.duration_minutes} min • PIN: {statistics.game_pin}
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back to Home
            </button>
          </div>

          {/* Overall Performance Summary */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Overall Performance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: aggregate_stats.class_average_percent.toFixed(1) + '%', label: 'Class Average', bgColor: 'bg-blue-50', textColor: 'text-blue-600', delay: 0 },
                { value: aggregate_stats.total_players, label: 'Students', bgColor: 'bg-green-50', textColor: 'text-green-600', delay: 100 },
                { value: aggregate_stats.std_deviation.toFixed(1) + '%', label: 'Std. Deviation', bgColor: 'bg-purple-50', textColor: 'text-purple-600', delay: 200 },
                { value: aggregate_stats.total_questions, label: 'Questions', bgColor: 'bg-orange-50', textColor: 'text-orange-600', delay: 300 }
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className={`text-center p-4 ${stat.bgColor} rounded-lg`}
                  style={{
                    animation: 'slideUp 0.6s ease-out',
                    animationDelay: `${stat.delay}ms`,
                    animationFillMode: 'both'
                  }}
                >
                  <div className={`text-3xl font-bold ${stat.textColor}`}>{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Score Distribution */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Score Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scoreDistData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="count"
                  fill="#f97316"
                  name="Number of Students"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Actionable Insights */}
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 rounded-lg shadow-lg border-2 border-orange-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Actionable Insights</h2>

            {questionsNeedingReview.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-red-700 mb-2">Topics to Review in Next Class:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {questionsNeedingReview.map((q) => (
                    <li key={q.index} className="text-gray-700">
                      <span className="font-medium">Q{q.index}:</span> {q.fullText} ({q.correctPercent.toFixed(1)}% correct)
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {strongQuestions.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-green-700 mb-2">Strong Areas (Well Understood):</h3>
                <ul className="list-disc list-inside space-y-1">
                  {strongQuestions.slice(0, 3).map((q) => (
                    <li key={q.index} className="text-gray-700">
                      <span className="font-medium">Q{q.index}:</span> {q.fullText} ({q.correctPercent.toFixed(1)}% correct)
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-2">Class Coherence:</h3>
              <p className="text-gray-700">
                {aggregate_stats.std_deviation < 15 ? (
                  <>Standard deviation of {aggregate_stats.std_deviation.toFixed(1)}% indicates a <strong>highly coherent</strong> class with similar understanding levels.</>
                ) : aggregate_stats.std_deviation < 25 ? (
                  <>Standard deviation of {aggregate_stats.std_deviation.toFixed(1)}% shows <strong>moderate variation</strong> in understanding across students.</>
                ) : (
                  <>Standard deviation of {aggregate_stats.std_deviation.toFixed(1)}% reveals <strong>significant variation</strong> - consider differentiated instruction or review sessions.</>
                )}
              </p>
            </div>
          </div>

          {/* Question Difficulty Heat Map Timeline */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Question Difficulty Flow</h2>
            <div className="flex items-center justify-center space-x-2 mb-4">
              {questionDifficultyData.map((q) => (
                <div key={q.index} className="flex flex-col items-center group relative">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white cursor-pointer transform transition-transform hover:scale-110 hover:shadow-lg"
                    style={{
                      backgroundColor: getDifficultyColor(q.correctPercent),
                      animation: 'popIn 0.5s ease-out',
                      animationDelay: `${q.index * 80}ms`,
                      animationFillMode: 'both'
                    }}
                    title={q.fullText}
                    onClick={() => {
                      const element = document.getElementById(`question-${q.index - 1}`);
                      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    {q.index}
                  </div>
                  <div
                    className="text-xs text-gray-600 mt-1"
                    style={{
                      animation: 'fadeIn 0.5s ease-out',
                      animationDelay: `${q.index * 80 + 200}ms`,
                      animationFillMode: 'both'
                    }}
                  >
                    {q.correctPercent.toFixed(0)}%
                  </div>

                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap z-10">
                    {q.questionText}
                    <br />
                    {q.correctPercent.toFixed(1)}% correct • {q.avgTime.toFixed(1)}s avg
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center space-x-6 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
                <span>Easy (&gt;80%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></div>
                <span>Medium (50-80%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
                <span>Hard (&lt;50%)</span>
              </div>
            </div>
          </div>

          {/* Network Health Summary */}
          {!statistics.network_health_summary && (
            <div className="bg-gray-50 rounded-lg shadow-lg border-2 border-gray-200 p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Network Health Analysis
              </h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <div className="text-yellow-700 font-semibold text-lg mb-1">⚠️ No Network Data Available</div>
                <p className="text-sm text-gray-600">
                  Network health statistics require player timing data. This could mean:
                </p>
                <ul className="text-sm text-gray-600 mt-2 text-left list-disc list-inside">
                  <li>No players answered any questions during this game</li>
                  <li>Players were using an older version of the app without timing support</li>
                  <li>Network timing data was not collected by the game server</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">
                  💡 Tip: Make sure players are using the latest version of the player app and actually answer questions during the game.
                </p>
              </div>
            </div>
          )}
          {statistics.network_health_summary && (
            <div className={`rounded-lg shadow-lg border-2 ${getNetworkHealthStatus().borderColor} ${getNetworkHealthStatus().bgColor} p-6 mb-8`}>
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setNetworkHealthCollapsed(!networkHealthCollapsed)}
              >
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Network Health Analysis
                  </h2>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    getNetworkHealthStatus().color === 'green' ? 'bg-green-100 text-green-800' :
                    getNetworkHealthStatus().color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {getNetworkHealthStatus().status}
                  </span>
                </div>
                <svg
                  className={`w-6 h-6 transition-transform ${networkHealthCollapsed ? '' : 'rotate-180'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {!networkHealthCollapsed && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Statistical analysis of network latency during the game. Only significant issues are highlighted.
                  </p>

              {/* Overall Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 text-center shadow">
                  <div className="text-2xl font-bold text-blue-600">
                    {statistics.network_health_summary.average_network_lag_ms.toFixed(0)}ms
                  </div>
                  <div className="text-xs text-gray-600">Average Lag</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow">
                  <div className="text-2xl font-bold text-green-600">
                    {statistics.network_health_summary.median_lag_ms.toFixed(0)}ms
                  </div>
                  <div className="text-xs text-gray-600">Median Lag</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow">
                  <div className="text-2xl font-bold text-purple-600">
                    {statistics.network_health_summary.std_dev_lag_ms.toFixed(0)}ms
                  </div>
                  <div className="text-xs text-gray-600">Std. Deviation</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow">
                  <div className="text-2xl font-bold text-orange-600">
                    {statistics.network_health_summary.sample_count}
                  </div>
                  <div className="text-xs text-gray-600">Samples</div>
                </div>
              </div>

              {/* Problematic Players */}
              {statistics.network_health_summary.problematic_players && statistics.network_health_summary.problematic_players.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                    ⚠️ Players with Network Issues ({statistics.network_health_summary.problematic_players.length})
                  </h3>
                  <div className="space-y-2">
                    {statistics.network_health_summary.problematic_players.map((player, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <div>
                          <span className="font-medium text-gray-900">Player {player.player_id.substring(0, 8)}...</span>
                          <span className="text-gray-600 ml-2">
                            {player.issue_count} question{player.issue_count > 1 ? 's' : ''} affected
                          </span>
                        </div>
                        <div className="text-red-600 font-semibold">
                          {player.average_lag_ms.toFixed(0)}ms avg
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    These players experienced lag significantly above average (mean + 1.5σ threshold).
                  </p>
                </div>
              )}

              {/* Problematic Questions */}
              {statistics.network_health_summary.problematic_questions && statistics.network_health_summary.problematic_questions.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">
                    ⚠️ Questions with Network Issues ({statistics.network_health_summary.problematic_questions.length})
                  </h3>
                  <div className="space-y-2">
                    {statistics.network_health_summary.problematic_questions.map((question, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <div>
                          <span className="font-medium text-gray-900">Question {question.question_id}</span>
                          <span className="text-gray-600 ml-2">
                            {question.players_affected} player{question.players_affected > 1 ? 's' : ''} affected
                          </span>
                        </div>
                        <div className="text-red-600 font-semibold">
                          {question.average_lag_ms.toFixed(0)}ms avg
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Multiple players experienced lag on these questions, possibly due to server/network conditions at that moment.
                  </p>
                </div>
              )}

              {/* All Clear Message */}
              {(!statistics.network_health_summary.problematic_players || statistics.network_health_summary.problematic_players.length === 0) &&
               (!statistics.network_health_summary.problematic_questions || statistics.network_health_summary.problematic_questions.length === 0) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-green-700 font-semibold text-lg mb-1">✓ Network Health Excellent</div>
                  <p className="text-sm text-gray-600">
                    No significant lag issues detected. All players had fair timing conditions.
                  </p>
                </div>
              )}
                </div>
              )}
            </div>
          )}

          {/* Per-Question Message Flow Visualization */}
          {statistics.timing_events && timingEventsByQuestion.size > 0 && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-8">
              <div
                className="flex justify-between items-center cursor-pointer mb-6"
                onClick={() => setPerQuestionCollapsed(!perQuestionCollapsed)}
              >
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900">Per-Question Network Performance</h2>
                </div>
                <svg
                  className={`w-6 h-6 transition-transform ${perQuestionCollapsed ? '' : 'rotate-180'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {!perQuestionCollapsed && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <p className="text-sm text-gray-600">
                      Detailed message flow analysis for each question. Click to expand and see timing breakdowns.
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpandAll();
                      }}
                      className="px-4 py-2 bg-blue-500 text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      {expandAll ? 'Collapse All' : 'Expand All'}
                    </button>
                  </div>

              <div className="space-y-3">
                {Array.from(timingEventsByQuestion.entries()).map(([questionId, events], index) => {
                  const stats = questionTimingStats.get(questionId);
                  const questionData = quiz_snapshot.questions.find((q: any) => q.id === questionId);
                  const questionNumber = quiz_snapshot.questions.indexOf(questionData) + 1;
                  const questionText = questionData?.question || `Question ${questionNumber}`;
                  const isExpanded = expandedQuestions.has(questionId);

                  if (!stats) return null;

                  return (
                    <div
                      key={questionId}
                      className={`border rounded-lg transition-all ${
                        stats.issueCount > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      {/* Question Summary Header (Clickable) */}
                      <div
                        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleQuestion(questionId)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3 flex-1">
                            {/* Question Number */}
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold text-sm">
                              {questionNumber}
                            </div>

                            {/* Question Title (truncated) */}
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 truncate">
                                {questionText.length > 80 ? questionText.substring(0, 80) + '...' : questionText}
                              </div>
                              <div className="flex gap-4 text-xs text-gray-600 mt-1">
                                <span>{stats.playerCount} players</span>
                                <span>Avg: {stats.avgLatency.toFixed(0)}ms</span>
                                <span>Max: {stats.maxLatency.toFixed(0)}ms</span>
                                {stats.issueCount > 0 && (
                                  <span className="text-yellow-700 font-semibold">
                                    ⚠️ {stats.issueCount} player{stats.issueCount > 1 ? 's' : ''} with high latency
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expand/Collapse Icon */}
                          <div className="ml-4">
                            <svg
                              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Message Flow Visualization */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 p-4 bg-gray-50">
                          <MessageFlowVisualization
                            timingEvents={events}
                            questionNumber={questionNumber}
                            questionText={questionText}
                            players={statistics.player_results.map((pr: any) => ({
                              id: pr.player_id,
                              name: pr.player_name,
                            }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
                </div>
              )}
            </div>
          )}

          {/* Question-by-Question Breakdown */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Question-by-Question Analysis</h2>
            <div className="space-y-6">
              {question_stats.map((q, idx) => {
                const questionData = quiz_snapshot.questions[idx];
                const answerDistData = Object.entries(q.answer_distribution).map(([choiceIdx, count]) => ({
                  choice: questionData.answers[parseInt(choiceIdx)],
                  count: count as number,
                  isCorrect: parseInt(choiceIdx) === questionData.correct_index,
                }));

                return (
                  <div key={q.question_id} id={`question-${idx}`} className="border-l-4 pl-4 scroll-mt-24" style={{ borderColor: getDifficultyColor(q.correct_percentage) }}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900">
                          Q{idx + 1}: {questionData.question}
                        </h3>
                        {questionData.explanation && (
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>Explanation:</strong> {questionData.explanation}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-2xl font-bold" style={{ color: getDifficultyColor(q.correct_percentage) }}>
                          {q.correct_percentage.toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-600">
                          {q.correct_count}/{q.total_answered} correct
                        </div>
                        <div className="text-xs text-gray-600">
                          Avg: {q.average_time_seconds.toFixed(1)}s
                        </div>
                      </div>
                    </div>

                    {/* Answer Distribution */}
                    <div className="mt-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Answer Distribution:</h4>
                      <div className="space-y-1">
                        {answerDistData.map((answer, aIdx) => {
                          const percentage = q.total_answered > 0 ? (answer.count / q.total_answered) * 100 : 0;
                          return (
                            <div key={aIdx} className="flex items-center">
                              <div className="w-48 text-sm text-gray-700 truncate" title={answer.choice}>
                                {answer.choice} {answer.isCorrect && '✓'}
                              </div>
                              <div className="flex-1 mx-2">
                                <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                                  <div
                                    className={`h-full flex items-center justify-end pr-2 text-white text-xs font-semibold ${
                                      answer.isCorrect ? 'bg-green-500' : 'bg-red-400'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                  >
                                    {percentage > 10 && `${percentage.toFixed(0)}%`}
                                  </div>
                                </div>
                              </div>
                              <div className="w-16 text-right text-sm text-gray-600">
                                {answer.count} ({percentage.toFixed(0)}%)
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Identify common misconception */}
                      {(() => {
                        const wrongAnswers = answerDistData.filter(a => !a.isCorrect && a.count > 0);
                        if (wrongAnswers.length > 0) {
                          const mostCommonWrong = wrongAnswers.reduce((prev, current) =>
                            current.count > prev.count ? current : prev
                          );
                          if (mostCommonWrong.count >= q.total_answered * 0.2) {
                            return (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                                <strong>Common Misconception:</strong> {mostCommonWrong.count} students selected "{mostCommonWrong.choice}"
                              </div>
                            );
                          }
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default QuizStatistics;
